import { CheckCircle2, ArrowRight } from 'lucide-react';
import Button from '../../components/ui/Button';
import { invitationUrlSegment, jobUrlSegment } from '../../lib/slug';
import type { GlobalRole } from '../../types';
import type { JobProgressTally, WorkspaceScript } from './scriptWorkspaceTypes';

interface Props {
  role: GlobalRole | undefined;
  script: WorkspaceScript;
  jobProgress?: JobProgressTally | null;
  navigate: (path: string) => void;
}

function buildReturnTarget(role: GlobalRole | undefined, script: WorkspaceScript): {
  href: string;
  label: string;
} {
  const job = script.job;
  const jobSeg = job ? jobUrlSegment({ id: job.id, title: job.title, urlSlug: job.urlSlug }) : '';
  const invSlugForKol =
    script.invitation &&
    invitationUrlSegment({
      id: script.invitation.id,
      urlSlug: script.invitation.urlSlug,
      job: job ? { title: job.title, urlSlug: job.urlSlug } : undefined,
    });
  switch (role) {
    case 'BRAND':
      return {
        href: `/brand/jobs/${encodeURIComponent(jobSeg)}?tab=progress`,
        label: 'Back to campaign progress',
      };
    case 'AGENCY':
      return {
        href: `/agency/campaigns/${encodeURIComponent(jobSeg)}?tab=submissions`,
        label: 'Back to campaign submissions',
      };
    case 'KOL':
      return invSlugForKol
        ? { href: `/kol/invitations/${encodeURIComponent(invSlugForKol)}`, label: 'Back to your invitation' }
        : { href: '/kol/jobs', label: 'Back to your jobs' };
    case 'ADMIN':
      return { href: '/admin/campaigns', label: 'Back to campaigns' };
    default:
      return { href: '/', label: 'Back to home' };
  }
}

export default function StudioFinalCongrats({ role, script, jobProgress, navigate }: Props) {
  const target = buildReturnTarget(role, script);
  const tally = jobProgress;

  return (
    <div className="rounded-lg border border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-white p-5 shadow-sm dark:border-emerald-800 dark:from-emerald-950/30 dark:via-gray-900 dark:to-gray-900">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-8 w-8 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-[#22211d] dark:text-gray-100">
            Congratulations — campaign deliverable approved!
          </h3>
          <p className="mt-1 text-sm text-[#77756f] dark:text-gray-400">
            The brand has signed off on this submission. Nice work pushing it across the line.
          </p>

          {tally && tally.totalApproved > 0 && (
            <div className="mt-4 rounded-md border border-[#d4d1ca] bg-white px-3 py-3 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#77756f] dark:text-gray-400">
                Campaign progress
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[#22211d] dark:text-gray-100">
                <span>
                  <strong className="text-emerald-700 dark:text-emerald-400">{tally.completed}</strong>
                  <span className="text-[#77756f] dark:text-gray-400"> / {tally.totalApproved} completed</span>
                </span>
                <span className="text-[#77756f] dark:text-gray-500">·</span>
                <span>
                  <strong className="text-[#01696f] dark:text-teal-400">{tally.inProgress}</strong>
                  <span className="text-[#77756f] dark:text-gray-400"> in progress</span>
                </span>
                <span className="text-[#77756f] dark:text-gray-500">·</span>
                <span>
                  <strong className="text-[#a8a6a1] dark:text-gray-300">{tally.pending}</strong>
                  <span className="text-[#77756f] dark:text-gray-400"> pending</span>
                </span>
              </div>
            </div>
          )}

          <div className="mt-4">
            <Button
              type="button"
              size="sm"
              className="h-9 gap-1 bg-emerald-700 text-sm hover:bg-emerald-800"
              onClick={() => navigate(target.href)}
            >
              {target.label}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
